import type { MetaFunction } from 'react-router'
import { Link, useLoaderData } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { FloatingCTA } from '~/components/floating-cta'
import { InlineLeadCapture } from '~/components/inline-lead-capture'
import { TrustStrip } from '~/components/trust-strip'
import { BreadcrumbSchema, FAQSchema, StructuredData } from '~/components/structured-data'
import { ArticleSchema } from '~/components/seo-content'

const CEE_FAQS = [
  { question: "Qu'est-ce que les Certificats d'Économies d'Énergie (CEE) ?", answer: "Les CEE sont un dispositif obligeant les fournisseurs d'énergie (EDF, Engie, TotalEnergies) à financer des travaux d'économie d'énergie chez les particuliers. Le Pôle National des CEE délivre des certificats aux fournisseurs en échange de leur participation. Les particuliers reçoivent une prime, un chèque ou une remise immédiate selon le fournisseur." },
  { question: "Quelles sont les opérations CEE éligibles en 2026 ?", answer: "19 opérations standardisées sont éligibles : isolation des combles, isolation des murs, isolation des planchers, pompe à chaleur air/eau, chaudière granulés, ventilation double flux, chauffe-eau thermodynamique, audit énergétique, etc. Chaque opération a un montant fixe défini par arrêté ministériel." },
  { question: "Comment cumuler CEE avec MaPrimeRénov' ?", answer: "Les CEE sont cumulables avec MaPrimeRénov', l'éco-PTZ et la TVA réduite à 5,5 %. Ordre de calcul : MaPrimeRénov' déduite du devis, puis CEE déduite du reste à charge, puis éventuellement éco-PTZ pour financer le solde. Le ménage paie uniquement le reste à charge final." },
  { question: "Y a-t-il des bonus pour les ménages modestes ?", answer: "Oui, les CEE 'Coup de Pouce' offrent des montants bonifiés pour les ménages modestes et très modestes (selon barème ANAH). Par exemple, pour le remplacement d'une chaudière fioul par une PAC : 4 000 € pour les ménages modestes, 5 000 € pour les très modestes, contre 2 500 € en standard." },
  { question: "Qui peut bénéficier des CEE ?", answer: "Tous les particuliers, propriétaires ou locataires d'un logement de plus de 2 ans (résidence principale ou secondaire). Les copropriétés bénéficient aussi de CEE collectifs. L'artisan qui réalise les travaux doit être certifié RGE pour les opérations énergétiques." },
]

const CEE_OPERATIONS_LIST = [
  'Isolation combles perdus', 'Isolation combles aménagés', 'Isolation murs par l\'intérieur', 'Isolation murs par l\'extérieur',
  'Isolation plancher bas', 'Pompe à chaleur air/eau', 'Pompe à chaleur géothermique', 'Chaudière biomasse',
  'Chauffe-eau thermodynamique', 'Ventilation double flux', 'Audit énergétique', 'Système solaire combiné',
  'Chauffage solaire individuel', 'Régulation de chauffage', 'Calorifugeage', 'Émetteurs de chaleur',
  'Programmateur de chauffage', 'Plancher chauffant', 'Pompe à chaleur air/air',
]
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { CATEGORY_LABELS } from '~/lib/cee-types'
import {
  Zap, ArrowRight, AlertTriangle, FileText, BookOpen,
  Thermometer, Flame, Wind, Droplets, Sun,
} from 'lucide-react'
import { VoirAussi, CROSS_LINKS } from '~/components/voir-aussi'

export function headers() { return STATIC_PAGE_HEADERS }

export async function loader() {
  const { getOperationsByCategory } = await import('~/lib/cee-data.server')
  const { CEE_GUIDES } = await import('~/lib/cee-guides-data.server')
  const byCategory = getOperationsByCategory()
  // Strip heavy fields (faq, conditions, etc.) — component only needs
  // slug + name + code + montants for the catalog grid.
  const slim = Object.fromEntries(
    Object.entries(byCategory).map(([cat, ops]) => [
      cat,
      ops.map((o) => ({
        slug: o.slug, name: o.name, code: o.code,
        shortDesc: o.shortDesc,
        montantClassique: o.montantClassique,
        montantPrecarite: o.montantPrecarite,
        certification: o.certification,
      })),
    ]),
  )
  // Slim guides list — the "10 guides" section renders only the first 6 cards
  // (slug + shortTitle + readingMinutes). Heavy bodies stay server-side.
  const guides = CEE_GUIDES.slice(0, 6).map((g) => ({
    slug: g.slug,
    shortTitle: g.shortTitle,
    readingMinutes: g.readingMinutes,
  }))
  return { byCategory: slim, guides }
}

export const meta: MetaFunction = () => [
  { title: "Primes CEE 2026 : 19 opérations éligibles + cumul MaPrimeRénov'" },
  { name: "description", content: "Catalogue complet des Certificats d'Économies d'Énergie 2026 : 19 travaux éligibles (PAC, isolation, fenêtres, VMC), montants exacts, cumul avec MaPrimeRénov'. Artisans RGE." },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/cee' },
  { property: 'og:title', content: 'Primes CEE 2026 : catalogue des 19 opérations | AfficheServices' },
  { property: 'og:description', content: "Catalogue complet des primes CEE 2026 : PAC, isolation, biomasse, VMC, solaire. Montants par opération, conditions, cumul MaPrimeRénov'." },
  { property: 'og:url', content: 'https://www.afficheservices.fr/cee' },
]

const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  chauffage: Flame,
  isolation: Thermometer,
  ventilation: Wind,
  'eau-chaude': Droplets,
  solaire: Sun,
}

export default function CEECatalogPage() {
  const { byCategory, guides } = useLoaderData<typeof loader>()

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Rénovation énergétique', url: 'https://www.afficheservices.fr/renovation-energetique' },
        { name: 'CEE', url: 'https://www.afficheservices.fr/cee' },
      ]} />
      <ArticleSchema
        title="Primes CEE 2026 — Catalogue des 19 opérations éligibles + cumul MaPrimeRénov'"
        description="Guide complet des Certificats d'Économies d'Énergie 2026 : 19 opérations éligibles, montants par travaux, conditions de cumul avec MaPrimeRénov' et éco-PTZ. Artisans RGE certifiés."
        url="https://www.afficheservices.fr/cee"
      />
      <FAQSchema items={CEE_FAQS} />
      <StructuredData data={{
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: '19 opérations CEE éligibles 2026',
        numberOfItems: CEE_OPERATIONS_LIST.length,
        itemListElement: CEE_OPERATIONS_LIST.map((name, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name,
        })),
      }} />

      <section className="bg-gradient-to-br from-blue-900 via-indigo-900 to-violet-900 px-4 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-blue-200">
            <Zap className="h-3.5 w-3.5" /> Barèmes 2026
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-5xl">Primes CEE 2026 : 19 opérations + cumul MaPrimeRénov'</h1>
          <p className="mx-auto mt-4 max-w-3xl text-base text-blue-200 sm:text-lg">
            Les Certificats d'Économies d'Énergie (CEE) financent 19 opérations de rénovation énergétique en 2026,
            via les fournisseurs d'énergie obligés (EDF, TotalEnergies, Engie). Cumulables avec MaPrimeRénov',
            l'éco-PTZ et la TVA réduite à 5,5 %, ils permettent de couvrir 30 à 90 % du coût total selon les revenus.
            Bonifications « Coup de Pouce » jusqu'à 5 000 € pour les ménages modestes.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/simulateur-aides" className="rounded-xl bg-white px-5 py-3 text-sm font-bold text-blue-900 hover:bg-blue-50">
              Simuler mes aides
            </Link>
            <Link to="/cee/guides" className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-5 py-3 text-sm font-semibold text-white ring-1 ring-white/20 hover:bg-white/20">
              <BookOpen className="h-4 w-4" /> Lire les guides
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 lg:px-6">
        <div className="mb-6">
          <TrustStrip />
        </div>
        <div className="mb-8">
          <InlineLeadCapture
            heading="Touchez vos primes CEE en 48h"
            subheading="Code postal → 3 devis d'artisans RGE comparatifs"
          />
        </div>

        {/* À retenir — Key Takeaways for AI search optimization */}
        <section className="rounded-2xl border-2 border-blue-200 bg-blue-50/40 p-6">
          <h2 className="text-base font-bold uppercase tracking-wider text-blue-700">À retenir</h2>
          <ul className="mt-3 space-y-2 text-sm leading-relaxed text-slate-700">
            <li className="flex gap-2"><Zap className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" /> 19 opérations standardisées éligibles en 2026 : isolation (combles, planchers), pompes à chaleur, chaudière biomasse, ventilation, audit énergétique, chauffe-eau thermodynamique.</li>
            <li className="flex gap-2"><Zap className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" /> Coup de Pouce Chauffage : jusqu'à 5 000 € pour le remplacement d'une chaudière fioul par une PAC chez les ménages très modestes.</li>
            <li className="flex gap-2"><Zap className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" /> Ordre chronologique strict : <strong>engagement CEE signé AVANT le devis de l'artisan</strong>, sinon prime définitivement perdue.</li>
            <li className="flex gap-2"><Zap className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" /> Artisan certifié RGE obligatoire pour la prestation concernée (QualiPAC pour PAC, Qualibat pour isolation, etc.).</li>
            <li className="flex gap-2"><Zap className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" /> Cumul avec MaPrimeRénov' + éco-PTZ + TVA 5,5 % : reste à charge possible de 0 € pour les ménages Bleu sur certaines opérations.</li>
          </ul>
        </section>

        {/* Methodology — how CEE work technically */}
        <section className="mt-10 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-xl font-bold text-slate-900">Comment se calcule le montant d'une prime CEE ?</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Contrairement à MaPrimeRénov' (montants fixes par opération), les primes CEE varient selon
            quatre paramètres techniques. Comprendre la formule permet de comparer les offres entre opérateurs.
          </p>
          <ol className="mt-4 space-y-3 text-sm text-slate-700">
            <li>
              <strong className="text-slate-900">1. Volume de kWh cumac généré</strong> — Chaque opération produit un volume d'énergie économisée
              sur sa durée de vie, exprimé en kWh cumac (cumulé actualisé). Exemple : isolation des combles
              perdus en zone H1 sur 100 m² = environ 600 000 kWh cumac.
            </li>
            <li>
              <strong className="text-slate-900">2. Prix du kWh cumac sur le marché EEX</strong> — Les obligés rachètent les certificats sur le marché
              de l'énergie. Cours 2026 : 7-9 €/MWh cumac selon la période. Une obligation augmentée par les
              pouvoirs publics fait monter le prix.
            </li>
            <li>
              <strong className="text-slate-900">3. Bonification Coup de Pouce</strong> — Pour les ménages modestes ou très modestes, certaines
              opérations bénéficient d'un coefficient multiplicateur (×2 à ×3) : remplacement chaudière fioul,
              isolation des combles. C'est ici que se trouve le vrai gisement d'aide.
            </li>
            <li>
              <strong className="text-slate-900">4. Marge opérateur</strong> — L'obligé (EDF, TotalEnergies) ou son mandataire prélève une marge de
              20-40 % entre la valeur de marché et la prime versée. Comparer 3 offres CEE peut multiplier la
              prime par 1,5-2× pour le même chantier.
            </li>
          </ol>
          <p className="mt-4 text-xs text-slate-500">
            En pratique, vous obtenez une estimation de prime en quelques clics sur les sites des obligés.
            Toujours comparer 2-3 offres avant de signer l'engagement (qui doit précéder le devis).
          </p>
        </section>

        {/* 3 worked examples */}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-slate-900">3 exemples de calcul concrets</h2>
          <div className="mt-6 space-y-5">
            <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Cas 1 : Coup de Pouce Chauffage (PAC)</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                <strong>Brigitte et Pierre</strong>, retraités à Reims, RFR 24 000 € (très modeste / Bleu).
                Remplacement de leur chaudière fioul par une PAC air/eau. Devis artisan RGE QualiPAC : 13 500 € TTC.
                <strong>Coup de Pouce CEE</strong> bonifié pour ménage très modeste : <strong>5 000 €</strong>.
                Cumulé avec MaPrimeRénov' Bleu (5 000 €) + Bonus sortie passoire (1 000 €) = 11 000 € d'aides.
                Reste à charge : 2 500 €, finançable par éco-PTZ. Économies de 1 200 €/an sur le chauffage.
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Cas 2 : Coup de Pouce Isolation</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                <strong>Mehdi et Anaïs</strong>, propriétaires d'une maison de 120 m² à Toulouse, RFR 38 000 € (modeste / Jaune).
                Isolation de 100 m² de combles perdus en laine de roche soufflée. Devis isolateur RGE Qualibat : 2 800 €.
                <strong>CEE Coup de Pouce Isolation Jaune</strong> : <strong>2 200 €</strong>. Cumulé avec MaPrimeRénov' Jaune
                (20 €/m² × 100 m² = 2 000 €) = 4 200 € d'aides totales — soit <strong>plus que le devis lui-même</strong>.
                Le « surplus » est en fait imputable à la valeur de marché des CEE supérieure au coût des matériaux.
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Cas 3 : VMC double flux (intermédiaire)</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                <strong>Hélène</strong>, propriétaire célibataire d'un appartement T3 à Nantes, RFR 42 000 € (intermédiaire / Violet).
                Installation d'une VMC double flux haut rendement. Devis artisan RGE Qualifelec : 4 500 €.
                <strong>Prime CEE standard Violet</strong> : <strong>800 €</strong> (pas éligible au Coup de Pouce intermédiaire).
                MaPrimeRénov' Violet : 1 500 €. Total aides : 2 300 €. Reste à charge : 2 200 €. ROI 7 ans
                via réduction des pertes thermiques de la ventilation.
              </p>
            </div>
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-xl font-bold text-slate-900">Comment fonctionnent les CEE ?</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {[
              { num: '1', title: 'Engagement CEE signé AVANT devis', desc: "L'ordre chronologique est strict : engagez un opérateur CEE (EDF, TotalEnergies, mandataire) avant le devis de l'artisan. Sinon, prime perdue." },
              { num: '2', title: 'Artisan RGE uniquement', desc: "Le geste concerné doit être réalisé par un artisan certifié RGE (QualiPAC, QualiBois, etc.). Sans RGE, toutes les aides sont annulées." },
              { num: '3', title: "Cumul avec MaPrimeRénov'", desc: "Les CEE et MaPrimeRénov' sont cumulables. Pour une PAC air/eau chez un ménage modeste, le cumul peut atteindre 9 500 €." },
            ].map((step) => (
              <div key={step.num} className="rounded-xl border border-border bg-white p-5 shadow-xs">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">{step.num}</span>
                <h3 className="mt-3 text-sm font-bold text-slate-900">{step.title}</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{step.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {(['chauffage', 'isolation', 'ventilation', 'eau-chaude', 'solaire'] as const).map((cat) => {
          const ops = byCategory[cat]
          if (!ops || ops.length === 0) return null
          const Icon = CATEGORY_ICONS[cat] ?? Zap
          return (
            <section key={cat} className="mt-12">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">{CATEGORY_LABELS[cat]}</h2>
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
                  {ops.length} opération{ops.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {ops.map((op) => (
                  <Link
                    key={op.slug}
                    to={`/cee/${op.slug}`}
                    className="group rounded-xl border border-border bg-white p-5 shadow-xs transition-all hover:border-primary hover:shadow-md"
                  >
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-mono text-slate-400">{op.code}</p>
                        <h3 className="mt-1 text-sm font-bold text-slate-900 group-hover:text-primary">
                          {op.name}
                        </h3>
                        <p className="mt-1 text-xs text-slate-500 line-clamp-2">{op.shortDesc}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 group-hover:text-primary" />
                    </div>
                    <div className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-xs">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Classique</span>
                        <span className="font-semibold text-slate-900">{op.montantClassique}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-emerald-600">Précarité</span>
                        <span className="font-semibold text-emerald-700">{op.montantPrecarite}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )
        })}

        <section className="mt-16 rounded-2xl border border-border bg-slate-50 p-6 lg:p-10">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100">
              <BookOpen className="h-5 w-5 text-indigo-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">10 guides détaillés</h2>
          </div>
          <p className="mt-2 text-sm text-slate-600">
            Cumul, ordre chronologique, fraudes RGE, copropriété — toutes les questions techniques auxquelles personne ne répond clairement.
          </p>
          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            {guides.map((g) => (
              <Link
                key={g.slug}
                to={`/cee/guides/${g.slug}`}
                className="group flex items-start gap-3 rounded-xl bg-white p-4 ring-1 ring-slate-200 hover:ring-primary"
              >
                <FileText className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-900 group-hover:text-primary">{g.shortTitle}</p>
                  <p className="mt-0.5 text-xs text-slate-500">{g.readingMinutes} min de lecture</p>
                </div>
              </Link>
            ))}
          </div>
          <Link
            to="/cee/guides"
            className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
          >
            Voir les 10 guides <ArrowRight className="h-4 w-4" />
          </Link>
        </section>

        <section className="mt-12 rounded-2xl border border-amber-200 bg-amber-50 p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
            <div>
              <h3 className="text-sm font-bold text-amber-900">Démarchage CEE interdit depuis 2020</h3>
              <p className="mt-1 text-sm text-amber-800">
                Tout appel, SMS ou email non sollicité relatif aux CEE est illégal. Toute offre "100% gratuite" est une arnaque
                (reste à charge minimum 10 % imposé par la loi). Signalez sur{' '}
                <a href="https://signal.conso.gouv.fr" target="_blank" rel="noreferrer" className="font-semibold underline">
                  signal.conso.gouv.fr
                </a>
                .
              </p>
            </div>
          </div>
        </section>

        <VoirAussi links={CROSS_LINKS.renovation} />

        <section className="mt-12 text-center">
          <h2 className="text-2xl font-bold text-slate-900">Prêt à démarrer votre projet ?</h2>
          <p className="mt-2 text-sm text-slate-500">Recevez 3 devis gratuits d'artisans RGE certifiés près de chez vous.</p>
          <Link
            to="/devis-gratuit"
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold text-white hover:bg-primary-hover"
          >
            Demander mes devis gratuits <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>

      <FloatingCTA label="Comparez 3 devis avec primes CEE" subtitle="Artisans RGE · sous 48h" whatsappMessage="Bonjour, je souhaite obtenir 3 devis avec primes CEE." />
      <Footer />
    </div>
  )
}
